import klLockupLight from '../assets/kl-lockup-light.webp';
import klLockupDark from '../assets/kl-lockup-dark.webp';

/**
 * Who built the thing.
 *
 * Rendered by the authenticated shell (Layout) and by the sign-in page, so it is on every
 * screen - the 404 lives inside the shell too. In the shell it is inset to the content
 * column so it lines up with the page rather than running behind the rail; on the sign-in
 * screen there is no rail and it spans the window. See .shell .klfoot in shell.css.
 *
 * Static by design: the links and the wordmark are compiled in, not fetched. A footer that
 * called out to khanlogics.com on every page load would put a third-party request on the
 * critical path of an internal admin tool, and would blank itself the moment that site was
 * slow or the school's network blocked it.
 *
 * The lockup ships as two WebP files taken from khanlogics.com - light ink for the light
 * theme, reversed for the dark one - rather than one image recoloured with a filter, because
 * the mark is a two-tone origami form and no filter turns one into the other. Both are
 * rendered and the CSS shows whichever the theme calls for, so the swap costs no JavaScript
 * and cannot flash the wrong one during a theme change.
 *
 * Pinned to the bottom of the viewport rather than sitting at the end of the document, so it
 * is visible without scrolling. That makes it permanent chrome, which is why it is kept
 * short: every pixel it takes is a pixel the page underneath never gets back. The height is
 * declared once as --klfoot-h and the shell reserves exactly that much, so the two cannot
 * drift and leave content hidden underneath.
 */
const KHANLOGICS = {
  home: 'https://khanlogics.com/',
  solutions: 'https://khanlogics.com/solutions/',
  linkedIn: 'https://www.linkedin.com/company/khanlogics',
  contact: 'https://khanlogics.com/contact/',
  privacy: 'https://khanlogics.com/privacy-policy/',
  terms: 'https://khanlogics.com/terms/',
} as const;

export function AppFooter() {
  const external = { target: '_blank', rel: 'noreferrer noopener' } as const;

  return (
    <footer className="klfoot">
      <div className="klfoot__inner">
        <a className="klfoot__brand" href={KHANLOGICS.home} aria-label="KhanLogics" {...external}>
          <img className="klfoot__logo klfoot__logo--light" src={klLockupLight} alt="KhanLogics" />
          <img className="klfoot__logo klfoot__logo--dark" src={klLockupDark} alt="KhanLogics" />
        </a>

        <nav className="klfoot__nav" aria-label="KhanLogics">
          <a href={KHANLOGICS.solutions} {...external}>Solutions</a>
          <span aria-hidden="true">&middot;</span>
          <a href={KHANLOGICS.linkedIn} {...external}>LinkedIn</a>
          <span aria-hidden="true">&middot;</span>
          <a href={KHANLOGICS.contact} {...external}>Contact</a>
        </nav>

        <div className="klfoot__legal">
          <span className="klfoot__copy">
            &copy; {new Date().getFullYear()}. All rights reserved.
          </span>
          <span className="klfoot__legal-links">
            <a href={KHANLOGICS.privacy} {...external}>Privacy Policy</a>
            <span aria-hidden="true">&middot;</span>
            <a href={KHANLOGICS.terms} {...external}>Terms of Use</a>
          </span>
        </div>
      </div>
    </footer>
  );
}
